import React, { useState, useEffect, createContext, useContext } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Paper,
    Container,
    useTheme,
    useMediaQuery,
    Typography,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Card,
    CardContent,
    Fade,
    IconButton,
    Dialog as MuiDialog
} from '@mui/material';
import {
    ViewList,
    LocalShipping,
    ViewAgenda,
    WbSunny,
    Brightness3,
    WbTwilight,
    Close,
    DirectionsCar,
    Agriculture,
    Inventory,
    FilterList
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isFieldDriver } from '../utils/personnelType';

// Create context for vehicle type selection
const VehicleTypeContext = createContext();

// Custom hook to use vehicle type context
export const useVehicleType = () => {
    const context = useContext(VehicleTypeContext);
    if (!context) {
        throw new Error('useVehicleType must be used within a VehicleTypeProvider');
    }
    return context;
};

// Vehicle type provider component
export const VehicleTypeProvider = ({ children }) => {
    const [selectedType, setSelectedType] = useState('all');

    // Load saved selection from localStorage on mount
    useEffect(() => {
        const savedType = localStorage.getItem('selectedVehicleType');
        if (savedType && ['all', 'vans', 'flatbeds'].includes(savedType)) {
            setSelectedType(savedType);
        }
    }, []);

    // Save selection to localStorage whenever it changes
    const handleTypeChange = (newType) => {
        setSelectedType(newType);
        localStorage.setItem('selectedVehicleType', newType);
    };

    // Debug function - expose to window for testing
    useEffect(() => {
        window.clearVehicleTypeData = () => {
            localStorage.removeItem('selectedVehicleType');
            localStorage.removeItem('vehicleTypeOnboardingComplete');
            console.log('[DEBUG] Cleared vehicle type localStorage data. Refresh page to see onboarding.');
        };
        
        window.checkVehicleTypeData = () => {
            console.log('[DEBUG] Current localStorage data:', {
                selectedVehicleType: localStorage.getItem('selectedVehicleType'),
                vehicleTypeOnboardingComplete: localStorage.getItem('vehicleTypeOnboardingComplete')
            });
        };
    }, []);

    const value = {
        selectedType,
        setSelectedType: handleTypeChange,
        isAll: selectedType === 'all',
        isVans: selectedType === 'vans',
        isFlatbeds: selectedType === 'flatbeds'
    };

    return (
        <VehicleTypeContext.Provider value={value}>
            {children}
        </VehicleTypeContext.Provider>
    );
};

// Vehicle type selector component
const VehicleTypeSelector = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
    const { selectedType, setSelectedType } = useVehicleType();
    const { user } = useAuth();
    const location = useLocation();
    const hideDriverChecklistChrome = isMobile
        && location.pathname.includes('/forms/weekly-checklist')
        && isFieldDriver(user);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    // Check if user has seen the onboarding
    useEffect(() => {
        if (!user) {
            return;
        }

        const hasSeenOnboarding = localStorage.getItem('vehicleTypeOnboardingComplete');
        const hasExistingSelection = localStorage.getItem('selectedVehicleType');

        window.showVehicleOnboarding = () => {
            setShowOnboarding(true);
        };

        window.resetVehicleOnboarding = () => {
            localStorage.removeItem('vehicleTypeOnboardingComplete');
            localStorage.removeItem('selectedVehicleType');
            setSelectedType('all');
            console.log('[DEBUG] Reset onboarding. Refresh to see modal.');
        };

        // Show onboarding if user hasn't seen it and doesn't have a saved selection.
        // Skip it while a driver is on the mobile weekly checklist (that bar is hidden).
        if (hasSeenOnboarding || hasExistingSelection || hideDriverChecklistChrome) {
            return undefined;
        }
        const timer = setTimeout(() => {
            const onChecklist = window.location.pathname.includes('/forms/weekly-checklist');
            if (onChecklist && isFieldDriver(user)) return;
            setShowOnboarding(true);
        }, 1500);
        return () => clearTimeout(timer);

    }, [user, selectedType, hideDriverChecklistChrome]);

    // Update time every minute
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // Update every minute

        return () => clearInterval(timer);
    }, []);

    const handleTabChange = (event, newValue) => {
        const types = ['all', 'vans', 'flatbeds'];
        setSelectedType(types[newValue]);
    };

    const handleSelectType = (type) => {
        setSelectedType(type);
        setMobileFilterOpen(false);
    };

    const getTabIndex = () => {
        switch (selectedType) {
            case 'vans': return 1;
            case 'flatbeds': return 2;
            default: return 0;
        }
    };

    const getTabIcon = (type) => {
        switch (type) {
            case 'all': return <ViewList />;
            case 'vans': return <LocalShipping />;
            case 'flatbeds': return <ViewAgenda />;
            default: return <ViewList />;
        }
    };

    // Get greeting based on time of day
    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    // Get time-based icon
    const getTimeIcon = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return <WbSunny sx={{ color: '#FFA726' }} />;
        if (hour < 17) return <WbSunny sx={{ color: '#FF9800' }} />;
        return <Brightness3 sx={{ color: '#5C6BC0' }} />;
    };

    // Format date nicely
    const formatDate = () => {
        return currentTime.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get user's first name
    const getFirstName = () => {
        if (!user || !user.name) return 'there';
        return user.name.split(' ')[0];
    };

    // Handle onboarding completion
    const handleOnboardingComplete = (selectedOption) => {
        setSelectedType(selectedOption);
        localStorage.setItem('vehicleTypeOnboardingComplete', 'true');
        setShowOnboarding(false);
    };

    // Handle skip onboarding
    const handleSkipOnboarding = () => {
        localStorage.setItem('vehicleTypeOnboardingComplete', 'true');
        setShowOnboarding(false);
    };

    if (hideDriverChecklistChrome) {
        return null;
    }

    return (
        <>
            <Box 
                sx={{ 
                    position: 'sticky',
                    top: { xs: 64, md: 0 }, // Account for mobile AppBar
                    zIndex: theme.zIndex.appBar - 1,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
            >
                <Container maxWidth="xl" disableGutters sx={{ px: 1.5 }}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            bgcolor: 'transparent',
                            borderRadius: 0
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            minHeight: 56,
                            py: { xs: 1, md: 0 }
                        }}>
                            {/* Greeting and Date Section */}
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                flex: '0 0 auto',
                                mr: { xs: 2, md: 4 }
                            }}>
                                {!isMobile && (
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getTimeIcon()}
                                            <Typography 
                                                variant="subtitle1" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {getGreeting()}, {getFirstName()}!
                                            </Typography>
                                        </Box>
                                        <Chip 
                                            label={formatDate()}
                                            variant="outlined"
                                            size="small"
                                            sx={{ 
                                                borderColor: 'divider',
                                                color: 'text.secondary',
                                                fontSize: '0.875rem',
                                                '& .MuiChip-label': {
                                                    px: 1.5
                                                }
                                            }}
                                        />
                                    </Stack>
                                )}
                                {isMobile && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getTimeIcon()}
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {getGreeting()}, {getFirstName()}!
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Tabs Section (desktop) or Filter button (mobile) */}
                            <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
                                {isMobile ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<FilterList />}
                                        onClick={() => setMobileFilterOpen(true)}
                                    >
                                        Filter Vehicles
                                    </Button>
                                ) : (
                                    <Tabs
                                        value={getTabIndex()}
                                        onChange={handleTabChange}
                                        sx={{
                                            minHeight: 56,
                                            '& .MuiTabs-indicator': {
                                                backgroundColor: '#E94F37',
                                                height: 3
                                            },
                                            '& .MuiTab-root': {
                                                minHeight: 56,
                                                minWidth: 120,
                                                textTransform: 'none',
                                                fontSize: '1rem',
                                                fontWeight: 500,
                                                color: 'text.secondary',
                                                '&.Mui-selected': {
                                                    color: '#E94F37',
                                                    fontWeight: 600
                                                },
                                                '&:hover': {
                                                    color: '#E94F37',
                                                    opacity: 0.8
                                                }
                                            }
                                        }}
                                        variant="standard"
                                        centered={false}
                                    >
                                        <Tab icon={getTabIcon('all')} label="All" iconPosition="start" />
                                        <Tab icon={getTabIcon('vans')} label="Vans" iconPosition="start" />
                                        <Tab icon={getTabIcon('flatbeds')} label="Flatbeds" iconPosition="start" />
                                    </Tabs>
                                )}
                            </Box>

                            {/* Spacer for balance */}
                            <Box sx={{ flex: '0 0 auto', width: { xs: 0, md: 200 } }} />
                        </Box>
                    </Paper>
                </Container>
            </Box>

            {/* Mobile Vehicle Filter Bottom Sheet */}
            <MuiDialog
                open={mobileFilterOpen}
                onClose={() => setMobileFilterOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        position: 'fixed',
                        m: 0,
                        bottom: 0,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>Filter Vehicles</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.25}>
                        <Button
                            variant={selectedType === 'all' ? 'contained' : 'outlined'}
                            startIcon={<ViewList />}
                            onClick={() => handleSelectType('all')}
                            fullWidth
                        >
                            All Vehicles
                        </Button>
                        <Button
                            variant={selectedType === 'vans' ? 'contained' : 'outlined'}
                            startIcon={<LocalShipping />}
                            onClick={() => handleSelectType('vans')}
                            fullWidth
                        >
                            Vans
                        </Button>
                        <Button
                            variant={selectedType === 'flatbeds' ? 'contained' : 'outlined'}
                            startIcon={<ViewAgenda />}
                            onClick={() => handleSelectType('flatbeds')}
                            fullWidth
                        >
                            Flatbeds
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileFilterOpen(false)}>Close</Button>
                </DialogActions>
            </MuiDialog>

            {/* Onboarding Modal */}
            <Dialog
                open={showOnboarding}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    }
                }}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    pb: 2
                }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#E94F37' }}>
                            Welcome to Kranawetter Driver Portal! 🚛
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Let's get you started with the right view for your fleet
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={handleSkipOnboarding}
                        size="small"
                        sx={{ color: 'text.secondary' }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Choose how you'd like to view your fleet. You can change this anytime using the tabs above.
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Card 
                            sx={{ 
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: selectedType === 'all' ? '2px solid #E94F37' : '1px solid',
                                borderColor: selectedType === 'all' ? '#E94F37' : 'divider',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                            onClick={() => setSelectedType('all')}
                        >
                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                <ViewList sx={{ fontSize: 48, color: '#E94F37', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    All Vehicles
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    View your complete fleet including vans, flatbeds, and all other vehicle types
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card 
                            sx={{ 
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: selectedType === 'vans' ? '2px solid #E94F37' : '1px solid',
                                borderColor: selectedType === 'vans' ? '#E94F37' : 'divider',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                            onClick={() => setSelectedType('vans')}
                        >
                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                <LocalShipping sx={{ fontSize: 48, color: '#2196F3', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Vans Only
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Focus on your van fleet for delivery routes and urban operations
                                </Typography>
                            </CardContent>
                        </Card>

                        <Card 
                            sx={{ 
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                border: selectedType === 'flatbeds' ? '2px solid #E94F37' : '1px solid',
                                borderColor: selectedType === 'flatbeds' ? '#E94F37' : 'divider',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 4
                                }
                            }}
                            onClick={() => setSelectedType('flatbeds')}
                        >
                            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                <ViewAgenda sx={{ fontSize: 48, color: '#FF9800', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Flatbeds Only
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Manage your flatbed trucks for heavy-duty and construction work
                                </Typography>
                            </CardContent>
                        </Card>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleSkipOnboarding}
                        color="inherit"
                    >
                        Skip for now
                    </Button>
                    <Button 
                        onClick={() => handleOnboardingComplete(selectedType)}
                        variant="contained"
                        sx={{ 
                            bgcolor: '#E94F37',
                            '&:hover': { bgcolor: '#d63c2a' }
                        }}
                    >
                        Continue with {selectedType === 'all' ? 'All Vehicles' : selectedType === 'vans' ? 'Vans' : 'Flatbeds'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VehicleTypeSelector; 